import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef } from 'react';

import { requestComposerFocus } from '#modules/chat/components/Chats/ChatInput/composerFocus';
import {
  GameLogo,
  SessionProgressBar,
} from '#modules/finder/features/posts/components/SessionParts';
import { trackRoomChatOpened } from '#features/analytics/utils/sessionRoomFunnel';
import { MOBILE_ANCHOR_PROPS, MOBILE_WIDGET_MAX_HEIGHT } from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { useScrollTouchAction } from '#features/media-window/useScrollTouchAction';
import { Button, CommentIcon, UnreadBadge } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { Tooltip } from '#features/ui/components/Tooltip';
import { SessionVoiceTier } from '#features/voice/components/SessionVoiceTier';
import { useVoiceRoomState } from '#features/voice/hooks/useVoiceRoomState';
import { useTypingFieldFocused } from '#hooks/useTypingFieldFocused';
import type { SessionRoomEntry } from '../model/roomSessions';
import { useSessionRoomStore } from '../model/sessionRoomStore';
import {
  hasOpenDismissableLayer,
  isClosedPhase,
  resolveEscapeAction,
  type SessionRoomPhase,
} from './RoomChrome';
import { RoomDeviceButton } from './RoomDeviceButton';
import { RoomModeButton } from './RoomModeButton';
import { RoomPhaseLabel } from './RoomPhaseLabel';
import { RoomSurface } from './RoomSurface';
import { SessionRoomMenu } from './SessionRoomMenu';

/**
 * Раскрытая комната сессии на телефоне – плавающий виджет, а не шторка (01.10.2026).
 *
 * До этого раскрытый вид был `Drawer` во всю ширину, прибитый к нижнему краю: он ложился на
 * нижнюю навигацию, закрывал страницу подложкой и никуда не переносился – свёрнутая полоса
 * плавала над навигацией карточкой, а раскрытая вдруг становилась другим предметом. Теперь это
 * та же карточка, только выше: стоит там же, где полоса (`MOBILE_ANCHOR_PROPS`), над навигацией
 * и под верхней безопасной зоной, и переезжает к верхнему краю и обратно – тем же жестом, что
 * полоса, за любое место карточки. Полоски-ручки у неё нет: это не шторка. Рывок вниз от нижнего края сворачивает её обратно в полосу. Страница под
 * ней остаётся живой – подложки нет.
 *
 * Системная «назад» на Android по-прежнему сворачивает её: виджет остаётся открытым слоем
 * `role=dialog` (по нему `useNativeBackButton` решает потратить нажатие на закрытие) и сам
 * слушает Escape на документе, которым этот обработчик закрывает слои. Своей истории комната не
 * заводит – `pushState` ради закрытия ломает возврат на предыдущую страницу.
 *
 * Живого поля ввода здесь нет: 393px минус клавиатура – это либо хвост переписки, либо композер
 * под ней. Кнопка «Открыть чат» уводит в тред, где поле ввода работает по-настоящему и
 * клавиатура открывается по жесту человека.
 */

/** Кнопка подвала. 48px – ступень главного действия на телефоне, как в карточке заявки. */
const FOOTER_BUTTON_HEIGHT = '48px';

/**
 * Пока шторка открыта, полосы под ней нет.
 *
 * Полоса и шторка – два размера одного предмета, и показывать их одновременно значит показывать
 * человеку LIVE, остаток и микрофон трижды: в шапке шторки, в её ярусе голоса и ещё раз внизу.
 * Хуже того, микрофон полосы остаётся под пальцем рядом с микрофоном яруса – две кнопки об одном
 * и том же в двух сантиметрах друг от друга.
 *
 * Гасим `visibility`, а не `display`: полоса публикует резерв снизу (`useBottomSpaceVar`) через
 * `ResizeObserver`, а у элемента с `display: none` прямоугольник нулевой – наблюдатель тут же
 * пересчитал бы резерв во всю высоту экрана и отпихнул бы и страницу, и саму шторку. При
 * `visibility: hidden` коробка полосы остаётся на месте, резерв не врёт, а нажатия до неё не
 * доходят.
 *
 * Правило живёт здесь, а не в полосе, потому что о своей открытости знает только шторка; когда
 * полоса и шторка съедутся под одного хозяина (`SessionRoomHost`), это станет одним `null` в
 * его разметке.
 */
const SHEET_OPEN_CSS = `[data-testid='session-room-bar']{visibility:hidden}`;

/** Что показывает шторка в этой фазе. */
export type SessionRoomSheetSections = {
  /** Ярус голоса остаётся после конца сессии, пока голосовая комната открыта. */
  voiceTier: boolean;
  /** Строка-объяснение вместо яруса: «Сессия закончилась – чат остаётся». */
  hint: boolean;
  /** Хвост переписки и кнопка-композер. */
  feed: boolean;
  /**
   * Подвал есть только у попрощавшейся сессии, и в нём одна кнопка «Закрыть».
   *
   * У живой и предстоящей подвала нет вовсе: «Открыть сессию» стоит в кебабе шапки, и вторая
   * его копия внизу отнимала у шторки 60px – ровно тот этаж, ради которого её и открывают,
   * хвост переписки. Кнопка у нижнего края к тому же читается как главное действие шторки, а
   * главное действие здесь – голос и переписка, а не уход со шторки на страницу заявки.
   */
  footer: 'close' | null;
};

/**
 * Состав шторки по фазе.
 *
 * Главное здесь – что у законченной сессии переписка остаётся: в ней договариваются о следующей
 * игре, и убрать её вместе с голосом значило бы закрыть человеку единственную дверь обратно к
 * своим. Ярус голоса уходит только после закрытия разговора.
 */
export const sessionRoomSheetSections = (
  phase: SessionRoomPhase,
  inThisRoom = false,
  voiceRoomStatus?: string | null
): SessionRoomSheetSections => {
  switch (phase) {
    case 'upcoming':
    case 'live':
      return { voiceTier: true, hint: false, feed: true, footer: null };
    case 'ended':
    case 'cancelled':
      /*
        Разговор переживает сессию: оставляем микрофон и выход подключённому участнику,
        а вход - пока комната открыта. Закрыть виджет можно после закрытия самого разговора.
      */
      return inThisRoom || voiceRoomStatus === 'OPEN'
        ? { voiceTier: true, hint: true, feed: true, footer: null }
        : { voiceTier: false, hint: true, feed: true, footer: 'close' };
  }
};

type SessionRoomSheetProps = {
  /**
   * Экран переписки (только телефон): край свой (`chatAnchor`, по умолчанию верх) и переносится,
   * как везде (02.10.2026).
   */
  inChatThread?: boolean;
  session: SessionRoomEntry;
  phase: SessionRoomPhase;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Уйти на весь экран – на маршрут треда. `null`, пока чата нет: тогда и хвоста нет, и уводить
   * кнопке-композеру некуда.
   */
  onFullscreen: (() => void) | null;
  /** Закрыть плашку прощания. До конца сессии «Закрыть» не бывает вовсе (§14). */
  onClose: () => void;
  /**
   * Открыть «Настройки сессии» – существующий `SessionDetailsDialog` через обёртку по id. Тот же
   * проп, что у окна на десктопе: диалог живёт у хоста, потому что пункт меню закрывает шторку, и
   * диалог, живший внутри неё, уехал бы вместе с ней.
   */
  onOpenSessionSettings?: () => void;
  /**
   * Открыть «Позвать в сессию». Диалог тоже живёт у хоста; шторка закрывается до его открытия –
   * два модальных слоя на телефоне спорили бы за фокус и за «назад».
   */
  onOpenInvite?: () => void;
};

export const SessionRoomSheet = ({
  session,
  phase,
  open,
  onOpenChange,
  onFullscreen,
  onClose,
  onOpenSessionSettings,
  onOpenInvite,
  inChatThread = false,
}: SessionRoomSheetProps) => {
  const { t } = useLingui();

  const voiceListOpen = useSessionRoomStore((state) => state.voiceListOpen);
  const storedAnchor = useSessionRoomStore((state) => state.mobileAnchor);
  const chatAnchor = useSessionRoomStore((state) => state.chatAnchor);
  // В переписке край свой – по умолчанию верх, под шапкой; общий выбор там не действует.
  const mobileAnchor = inChatThread ? chatAnchor : storedAnchor;
  const setStoredAnchor = useSessionRoomStore((state) => state.setMobileAnchor);
  const setChatAnchor = useSessionRoomStore((state) => state.setChatAnchor);
  const setMobileAnchor = inChatThread ? setChatAnchor : setStoredAnchor;
  const setVoiceListOpen = useSessionRoomStore((state) => state.setVoiceListOpen);

  /*
    Голос спрашиваем ради одного вопроса кебаба – «есть ли что настраивать в звуке». Это чтение
    того же кеша, который держит ярус ниже: второго запроса не возникает.
  */
  const {
    inThisRoom,
    isPlatform,
    state: voiceState,
    roomStatus,
  } = useVoiceRoomState({
    sessionId: session.sessionId,
    voiceMode: session.voiceMode,
    teamLimit: session.teamLimit ?? 0,
  });

  const currentRoomStatus = roomStatus ?? session.voiceRoomStatus;
  // Оборвалась связь после конца сессии - ярус нужен ради кнопки возврата в разговор.
  const sections = sessionRoomSheetSections(
    phase,
    inThisRoom || (voiceState === 'connectionLost' && currentRoomStatus !== 'CLOSED'),
    currentRoomStatus
  );
  const closed = isClosedPhase(phase);

  /*
    Виджет переносится между нижним и верхним краем за любое место – как полоса; рывок вниз от
    нижнего края сворачивает его обратно в полосу. Раньше тянули только ручка с шапкой, и палец,
    положенный на ярус голоса, не двигал ничего (01.10.2026).
  */
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const onGripPointerDown = useCornerDrag({
    ref: widgetRef,
    canDrag: true,
    anchor: mobileAnchor,
    onCollapse: () => onOpenChange(false),
    onCorner: () => undefined,
    onAnchor: setMobileAnchor,
  });

  /*
    Поставили курсор в поле страницы – виджет сворачивается в полосу. Поля ввода у него нет, а
    стоит он у нижнего края, ровно над композером чужой переписки: без этого он закрывал бы то,
    во что человек собрался писать. Свёрнутая полоса на время набора сама уезжает наверх.
  */
  const typing = useTypingFieldFocused();

  // Середина отдаёт палец прокрутке, только когда ей есть куда прокручиваться.
  const scroller = useScrollTouchAction<HTMLDivElement>(open);

  useEffect(() => {
    if (open && typing) {
      onOpenChange(false);
    }
  }, [open, typing, onOpenChange]);

  /*
    Escape на документе сворачивает виджет: им же системная «назад» на Android закрывает
    открытые слои. Открытый внутри кебаб или выбор устройства забирает нажатие себе.
  */
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      const action = resolveEscapeAction({
        defaultPrevented: event.defaultPrevented,
        composing: event.isComposing,
        layerOpen: hasOpenDismissableLayer(document),
        inTextField: false,
      });

      if (action === 'collapse') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  /*
    Уход в тред за письмом – с курсором в поле.

    Кнопка-композер шторки нарисована полем ввода, а ведёт на маршрут: своего поля здесь нет и не
    будет (§9.2). Пока признак не проводили, человек попадал в тред с фокусом на `body` и делал
    второй тап по настоящему полю – кнопка обещала клавиатуру и не открывала её.

    Признак ставим ровно здесь, в обработчике нажатия: браузер открывает клавиатуру по фокусу
    только тогда, когда за фокусом стоит жест человека. Поставленный где-нибудь в эффекте после
    перехода, он дал бы курсор без клавиатуры – то есть ту же вторую попытку, только молча.
  */
  const openThreadToWrite = useCallback(() => {
    if (!onFullscreen) {
      return;
    }

    // Дверь в переписку - числитель замера комнаты. В шторке она одна, в окне их две.
    trackRoomChatOpened('sheet');

    if (session.chatId) {
      requestComposerFocus(session.chatId);
    }

    onFullscreen();
  }, [onFullscreen, session.chatId]);

  const chatAction = (
    <Tooltip
      content={t`post:session.room.chatCreating`}
      disabled={Boolean(onFullscreen)}
      openDelay={200}
    >
      <Box position="relative" flexShrink={0}>
        <IconButton
          variant="ghost"
          aria-label={onFullscreen ? t`Open chat` : t`post:session.room.chatCreating`}
          w={11}
          minW={11}
          h={11}
          color="fg.icon"
          disabled={!onFullscreen}
          onClick={openThreadToWrite}
        >
          {onFullscreen ? <CommentIcon /> : <Spinner size="sm" />}
        </IconButton>
        {session.unreadCount > 0 ? (
          <Box position="absolute" top={0.5} right={0.5} pointerEvents="none">
            <UnreadBadge count={session.unreadCount} size="sm" />
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );

  return (
    <>
      {open ? <style data-testid="session-room-sheet-css">{SHEET_OPEN_CSS}</style> : null}
      {open ? (
        <RoomSurface
          ref={widgetRef}
          data-testid="session-room-sheet"
          data-phase={phase}
          data-anchor={mobileAnchor}
          data-state="open"
          role="dialog"
          aria-modal={false}
          aria-label={t`post:session.room.sheetAria`}
          left={3}
          right={3}
          {...MOBILE_ANCHOR_PROPS[mobileAnchor]}
          maxH={MOBILE_WIDGET_MAX_HEIGHT}
          direction="column"
          overflow="hidden"
          pt={2}
          pb={3}
          cursor="grab"
          // Без этого палец на телефоне прокручивает страницу, и перенос не начинается.
          css={{ touchAction: 'none' }}
          onPointerDown={onGripPointerDown}
        >
          <Flex pl={4} pr={2} py={2} gap={2} align="center" flexShrink={0}>
            <Box flex={1} minW={0}>
              <Flex align="center" gap={2} minW={0}>
                {session.game?.icon ? (
                  <GameLogo icon={session.game.icon} color={session.game.iconColor} diameter={24} />
                ) : null}
                <Text textStyle="title" color="fg" lineClamp={1} minW={0}>
                  {session.game?.name
                    ? t`post:session.room.gameSessionTitle ${session.game.name}`
                    : t`post:session.room.sessionTitle`}
                </Text>
              </Flex>
              <RoomPhaseLabel
                phase={phase}
                startedAt={session.startedAt}
                expiredAt={session.expiredAt}
              />
            </Box>
            <Flex gap={1} align="center" flexShrink={0}>
              {/* Предварительный выбор устройств на поддерживаемых платформах. */}
              {isPlatform && sections.voiceTier ? (
                <RoomDeviceButton inThisRoom={inThisRoom} size="44px" />
              ) : null}
              <RoomModeButton mode="collapse" onClick={() => onOpenChange(false)} />
              <SessionRoomMenu
                postId={session.postId}
                sessionId={session.sessionId}
                hostId={session.hostId}
                phase={phase}
                inVoice={inThisRoom}
                chatId={session.chatId}
                startedAt={session.startedAt}
                expiredAt={session.expiredAt}
                size="mobile"
                onSessionSettings={onOpenSessionSettings}
                onLeft={() => onOpenChange(false)}
              />
            </Flex>
          </Flex>

          {phase === 'live' && session.startedAt && session.expiredAt ? (
            <Box px={4} pb={2} flexShrink={0}>
              <SessionProgressBar
                startedAt={session.startedAt}
                expiredAt={session.expiredAt}
                height="2px"
              />
            </Box>
          ) : null}

          {/*
            Прокручивается только середина: шапка с кебабом и подвал обязаны остаться на месте –
            именно за ними в шторку и лезут.
          */}
          <Box
            ref={scroller.ref}
            flex={1}
            minH={0}
            overflowY="auto"
            css={{ overscrollBehavior: 'contain', touchAction: scroller.touchAction }}
          >
            {/* Сцена 16:9 (фаза 3) – в DOM её нет, пока в комнате нет живого видео. */}
            {sections.voiceTier ? (
              <SessionVoiceTier
                sessionId={session.sessionId}
                postId={session.postId}
                density="mobile"
                expanded={voiceListOpen}
                onExpandedChange={setVoiceListOpen}
                onPersonOpen={() => onOpenChange(false)}
                actions={chatAction}
                onInvite={
                  !closed && onOpenInvite
                    ? () => {
                        onOpenChange(false);
                        onOpenInvite();
                      }
                    : undefined
                }
              />
            ) : null}

            {sections.feed && !sections.voiceTier && sections.footer !== 'close' ? (
              <Flex px={4} pt={3} justify="flex-end">
                {chatAction}
              </Flex>
            ) : null}
          </Box>

          {/*
            Подвал – только у попрощавшейся сессии. У живой и предстоящей все действия живут в
            кебабе шапки: «Открыть сессию», «Пригласить», настройки и выход спрашиваются там одним
            списком, и второй копии «Открыть сессию» внизу быть не должно – она отнимала этаж
            переписки и спорила с кебабом за роль главного действия.
          */}
          {sections.footer === 'close' ? (
            <Flex gap={2} px={4} pt={3} flexShrink={0}>
              {session.chatId && onFullscreen && phase !== 'cancelled' ? (
                <Button
                  variant="outline"
                  flex={1}
                  h={FOOTER_BUTTON_HEIGHT}
                  onClick={openThreadToWrite}
                >
                  {t`Open chat`}
                </Button>
              ) : null}
              <Button variant="outline" flex={1} h={FOOTER_BUTTON_HEIGHT} onClick={onClose}>
                {t`post:session.room.close`}
              </Button>
            </Flex>
          ) : null}
        </RoomSurface>
      ) : null}
    </>
  );
};
